/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import clsx from 'clsx'
import { reportError } from 'utils/errorReporter'
import UiStateView from 'components/Ui/UiStateView'
import { useTranslation } from 'react-i18next'

type ErrorBoundaryImplProps = {
    title: string
    description: string
    children: React.ReactNode
}

type ErrorBoundaryImplState = {
    hasError: boolean
}

class ErrorBoundaryImpl extends React.Component<
    ErrorBoundaryImplProps,
    ErrorBoundaryImplState
> {
    state = { hasError: false }

    static getDerivedStateFromError() {
        return { hasError: true }
    }

    async componentDidCatch(error: Error) {
        await reportError(error, { type: 'ErrorBoundary' })
    }

    render() {
        if (this.state.hasError) {
            return (
                <UiStateView
                    variant="error"
                    title={this.props.title}
                    description={this.props.description}
                    className={clsx('min-h-60 p-6')}
                />
            )
        }
        return this.props.children
    }
}

const ErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useTranslation()

    return (
        <ErrorBoundaryImpl
            title={t('Something went wrong')}
            description={t('Please refresh the page.')}
        >
            {children}
        </ErrorBoundaryImpl>
    )
}

export default ErrorBoundary
